import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import {FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators} from "@angular/forms";
import {NgSelectModule} from "@ng-select/ng-select";
import {RichEditorService} from "../../../../core_modules/common/components/rich-editor/rich-editor.service";
import {DomSanitizer, SafeHtml, SafeResourceUrl} from "@angular/platform-browser";
import {FileUploadService} from "../../../../admin_modules/services/file-upload.service";
import {auditTime} from "rxjs";
import {DynamicSectionFormSchema, FormField} from "../../dynamic-form-v2/form-schema.types";
import {NepaliInputComponent} from "../../nepali-input/nepali-input.component";
import {NgClass, NgTemplateOutlet} from "@angular/common";
import {RichEditorComponent} from "../../../../core_modules/common/components/rich-editor/rich-editor.component";
import {CommonCalendarAdBsComponent} from "../../calendar-ad-bs/common-calendar-ad-bs.component";
import {AmountCommaFormatterDirective} from "../../../../core_modules/directives/amount-comma-formatter.directive";
import {FormCardComponent} from "../../form-card/form-card.component";
import {FormCardHeaderComponent} from "../../form-card/form-card-header/form-card-header.component";
import {FormCardBodyComponent} from "../../form-card/form-card-body/form-card-body.component";

@Component({
  selector: 'app-dynamic-section-form',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    NgSelectModule,
    NepaliInputComponent,
    RichEditorComponent,
    CommonCalendarAdBsComponent,
    AmountCommaFormatterDirective,
    NgClass,
    NgTemplateOutlet,
    FormCardComponent,
    FormCardHeaderComponent,
    FormCardBodyComponent
  ],
  templateUrl: './dynamic-section-form.component.html',
  styleUrl: './dynamic-section-form.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DynamicSectionFormComponent implements OnInit, OnChanges {
// Array of cards instead of {sections / fields} — every field across every
  // card still lands in ONE flat FormGroup (this.form).
  @Input({required: true}) schema: DynamicSectionFormSchema = [];
  @Input() modelToPatchToForm: any = null;
  @Output() formChanges = new EventEmitter<FormGroup>();
  @Output() onChange = new EventEmitter<{ key: string; value: any }>();
  @Output() enterPressed = new EventEmitter<void>();
  @Output() fileSelected = new EventEmitter<{ event: Event; field: any }>();

  form!: FormGroup;
  submittedOnce = false;
  submitted = false;
  showPasswordMap: { [key: string]: boolean } = {};
  removingFiles: { [key: string]: boolean } = {};
  viewingFiles: { [key: string]: boolean } = {};

  uploadedFiles: {
    [key: string]: {
      file: File;
      url?: string;
      newFileName?: string;
      uploaded?: boolean;
      compressed?: 'Y' | 'N';
    };
  } = {};

  previewUrl: string | null = null;
  previewUrlSafe: SafeResourceUrl | null = null;
  previewFileName: string = '';
  uploadingFiles: { [key: string]: boolean } = {};

  @Input() entityId: number | null = null;
  @Input() entityIdKey: string = 'id';
  private tempEntityId: string | null = null;


  private fieldValidatorsMap = new Map<string, any[]>();
  private activePreviewKey: string | null = null;

  constructor(
    private fb: FormBuilder,
    private richEditorService: RichEditorService,
    private sanitizer: DomSanitizer,
    private fileUploadService: FileUploadService,
    private cdr: ChangeDetectorRef,
  ) {
  }

  ngOnInit() {
    this.buildForm();
    this.form.valueChanges.pipe(auditTime(3000)).subscribe(() => {
      this.handleCalculatedFields();
      // this.formChanges.emit(this.form);
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['schema'] && !changes['schema'].firstChange) {
      this.buildForm();
    }

    if (changes['modelToPatchToForm'] && this.form && this.modelToPatchToForm) {
      this.form.patchValue(this.modelToPatchToForm);
    }
  }


  private generateTempId(): string {
    return (crypto?.randomUUID?.() ?? `temp-${Date.now()}-${Math.random().toString(36).slice(2)}`);
  }

  private getEffectiveEntityId(): string {
    if (this.entityId != null) {
      return this.entityId.toString();
    }
    if (!this.tempEntityId) {
      this.tempEntityId = this.generateTempId();
    }
    return this.tempEntityId;
  }

  onFileSelected(event: Event, key: string): void {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;

    const file = input.files[0];
    this.uploadedFiles[key] = {file};

    const fields = this.getFields();
    const field = fields.find((f: any) => f.key === key);
    const documentType = field?.documentType ?? '';
    const subBucketName = (field as any)?.subBucketName ?? '';
    const compressed = field?.compressed ?? 'N';

    const effectiveId = this.getEffectiveEntityId();

    const formData = new FormData();
    formData.append('file', file);
    formData.append('documentType', documentType);
    formData.append('subBucketName', subBucketName);
    formData.append(this.entityIdKey, effectiveId);
    formData.append('compressed', compressed);

    this.uploadingFiles[key] = true;
    this.cdr.markForCheck();

    this.fileUploadService.upload(formData).subscribe({
      next: (response: any) => {
        let newFileName = response?.data?.newFileName;

        if (newFileName && newFileName.startsWith('null/')) {
          newFileName = newFileName.replace('null/', '');
        }

        this.uploadedFiles[key].url = response.fileUrl;
        this.uploadedFiles[key].newFileName = newFileName;
        this.uploadedFiles[key].uploaded = true;
        this.uploadedFiles[key].compressed = compressed;

        this.form.get(key)?.setValue(newFileName);
        this.uploadingFiles[key] = false;
        this.cdr.markForCheck();
      },
      error: (error: any) => {
        console.error('File upload failed', error);
        delete this.uploadedFiles[key];
        this.uploadingFiles[key] = false;
        this.cdr.markForCheck();
      },
    });
  }


  viewUploadedFile(key: string, field: any): void {
    const uploaded = this.uploadedFiles[key];
    const identifierToView = uploaded?.newFileName || field.currentFilePath;
    if (!identifierToView) return;

    const documentType = field?.documentType ?? '';

    this.viewingFiles[key] = true;
    this.activePreviewKey = key;
    this.cdr.markForCheck();

    this.fileUploadService.getFileUrl(identifierToView, documentType).subscribe({
      next: (response: any) => {
        this.viewingFiles[key] = false;

        if (this.activePreviewKey !== key) {
          this.cdr.markForCheck();
          return;
        }

        const url = response?.data;
        if (url) {
          this.previewUrl = url;
          this.previewUrlSafe = this.sanitizer.bypassSecurityTrustResourceUrl(url);
          this.previewFileName = uploaded?.file?.name || field.currentFileName || '';
        }
        this.cdr.markForCheck();
      },
      error: (error: any) => {
        console.error('Failed to get file URL', error);
        this.viewingFiles[key] = false;
        if (this.activePreviewKey === key) {
          this.activePreviewKey = null;
        }
        this.cdr.markForCheck();
      },
    });
  }

  closePreview(): void {
    this.previewUrl = null;
    this.previewUrlSafe = null;
    this.previewFileName = '';
    this.activePreviewKey = null;
    this.cdr.markForCheck();
  }

  isImagePreview(): boolean {
    if (!this.previewUrl) return false;
    const cleanUrl = this.previewUrl.split('?')[0].toLowerCase();
    return /\.(png|jpe?g|gif|webp|svg|bmp)$/.test(cleanUrl);
  }

  removeUploadedFile(key: string, field: any): void {
    if (this.activePreviewKey === key) {
      this.activePreviewKey = null;
    }
    const uploaded = this.uploadedFiles[key];

    const identifierToRemove = uploaded?.newFileName || field.currentFilePath;

    if (!identifierToRemove) {
      delete this.uploadedFiles[key];
      this.form.get(key)?.setValue(null);
      this.cdr.markForCheck();
      return;
    }

    const documentType = field?.documentType ?? '';

    this.removingFiles[key] = true;
    this.cdr.markForCheck();

    this.fileUploadService.delete(identifierToRemove, documentType).subscribe({
      next: () => {
        delete this.uploadedFiles[key];
        field.currentFileName = '';
        field.currentFilePath = '';
        this.form.get(key)?.setValue(null);

        this.removingFiles[key] = false;
        this.cdr.markForCheck();
      },
      error: (error: any) => {
        console.error('File removal failed', error);
        this.removingFiles[key] = false;
        this.cdr.markForCheck();
      },
    });
  }

  /**
   * Flattens every card's fields into a single list. This is the one place
   * that understands the card-based schema shape — everything below this
   * treats the result as a plain field list, exactly like the old flat
   * schema did.
   */
  private getFields(): FormField[] | any[] {
    if (!Array.isArray(this.schema) || this.schema.length === 0) {
      console.warn('Dynamic section form got empty schema...');
      return [];
    }
    return this.schema.flatMap((card) => card.fields || []);
  }

  private buildForm() {
    const group: Record<string, FormControl> = {};
    const fields = this.getFields();

    fields.forEach((f: any) => {
      const isCalculated = !!f.calculated;
      const isReadonly = !!f.readonly;

      const initialValue = this.resolveInitialValue(f);

      group[f.key] = new FormControl(
        {
          value: initialValue,
          disabled: isCalculated || isReadonly,
        },
        [...this.toValidators(f.validators)],
      );

      if (f.typeKey) {
        group[f.typeKey] = new FormControl('PERCENTAGE', []);
      }
    });

    this.form = this.fb.group(group);
    this.setupFieldDependencies(fields);

    this.tryPatchFromModel();

    setTimeout(() => this.handleCalculatedFields(), 0);
  }

  private resolveInitialValue(f: any): any {
    if (f.defaultValue !== undefined && f.defaultValue !== null) {
      return f.defaultValue;
    }

    if (f.type === 'CHECKBOX') {
      return false;
    }

    // NOTE: confirm this matches the actual type string used in your
    // schema for file/upload fields (e.g. 'FILE', 'UPLOAD', 'DOCUMENT').
    if (f.type === 'FILE' && (f.currentFilePath || f.currentFileName)) {
      return f.currentFilePath || f.currentFileName;
    }

    return null;
  }

  private setupFieldDependencies(fields: any[]) {
    fields.forEach((field) => {
      if (!field.dependsOn) return;

      const control = this.form.get(field.key);
      const dependentControl = this.form.get(field.dependsOn.key);

      if (!control || !dependentControl) return;

      if (!this.fieldValidatorsMap.has(field.key)) {
        this.fieldValidatorsMap.set(field.key, this.toValidators(field.validators));
      }

      const updateVisibility = () => {
        const shouldShow = this.isFieldVisible(field);

        if (shouldShow) {
          if (field.readonly) {
            control.disable({emitEvent: false});
          } else {
            control.enable({emitEvent: false});
          }
          control.setValidators(this.fieldValidatorsMap.get(field.key) || []);
        } else {
          control.clearValidators();
          control.setValue(null, {emitEvent: false});
          control.disable({emitEvent: false});
        }

        control.updateValueAndValidity({emitEvent: false});
      };

      updateVisibility();

      dependentControl.valueChanges.subscribe(() => {
        updateVisibility();
      });
    });
  }

  passwordValidator() {
    return (control: FormControl) => {
      const value = control.value;
      if (!value) return null;

      const valid =
        /[A-Z]/.test(value) &&
        /[a-z]/.test(value) &&
        /[0-9]/.test(value) &&
        /[!@#$%^&*]/.test(value);

      return valid ? null : {passwordStrength: true};
    };
  }

  passwordMatchValidator() {
    return (control: FormControl) => {
      if (!control.parent) return null;

      const password = control.parent.get('password')?.value;
      const confirm = control.value;

      if (!confirm) return null;
      if (password !== confirm) return {notMatch: true};
      return null;
    };
  }

  /**
   * Check if a field should be visible based on its dependencies.
   */
  isFieldVisible(field: FormField): boolean {
    if ((field as any).showWhen) {
      const cond = (field as any).showWhen;
      const value = this.form?.get(cond.key)?.value;
      return value === cond.value;
    }

    if (!field.dependsOn) return true;

    const dependsOn = field.dependsOn;
    const dependentControl = this.form?.get(dependsOn.key);

    if (!dependentControl) return false;

    const dependentValue = dependentControl.value;

    switch (dependsOn.condition || 'EXISTS') {
      case 'EXISTS':
        return dependentValue !== null && dependentValue !== undefined && dependentValue !== '';

      case 'EQUALS':
        return dependentValue === dependsOn.value;

      case 'NOT_EQUALS':
        return dependentValue !== dependsOn.value;

      case 'CONTAINS':
        if (Array.isArray(dependentValue)) {
          return dependentValue.includes(dependsOn.value);
        }
        return String(dependentValue).includes(String(dependsOn.value));

      default:
        return true;
    }
  }

  /**
   * Handle calculated fields when form values change.
   */
  private handleCalculatedFields() {
    if (!this.form) return;

    const fields = this.getFields();

    fields.forEach((field: any) => {
      if (field.calculated) {
        const result = this.calculateFieldValue(field);
        const control = this.form.get(field.key);

        if (control && result !== null && result !== undefined) {
          control.setValue(result, {emitEvent: false});
        }
      }
    });
  }

  private calculateFieldValue(field: any): any {
    if (!field.calculated || !this.form) return null;

    try {
      const {formula, dependencies} = field.calculated;

      const values: Record<string, any> = {};
      dependencies.forEach((dep: string) => {
        const control = this.form.get(dep);
        const rawValue = control ? control.value : null;
        values[dep] =
          rawValue === null || rawValue === undefined || rawValue === '' ? 0 : Number(rawValue);
      });

      const hasInvalidValues = dependencies.some((dep: string) => isNaN(values[dep]));
      if (hasInvalidValues) return null;

      const sortedDeps = [...dependencies].sort((a, b) => b.length - a.length);
      let expression = formula;

      sortedDeps.forEach((dep: string) => {
        const regex = new RegExp(`\\b${dep}\\b`, 'g');
        expression = expression.replace(regex, String(values[dep]));
      });

      const result = this.evaluateExpression(expression);
      return Math.round(result * 100) / 100;
    } catch (error) {
      console.error('Error calculating field:', field.key, error);
      return null;
    }
  }

  private evaluateExpression(expr: string): number {
    try {
      if (!expr || expr.trim() === '') return 0;

      const safeExpr = expr
        .replace(/Math\.pow/g, 'Math.pow')
        .replace(/Math\.sqrt/g, 'Math.sqrt')
        .replace(/Math\.abs/g, 'Math.abs')
        .replace(/Math\.round/g, 'Math.round')
        .replace(/Math\.floor/g, 'Math.floor')
        .replace(/Math\.ceil/g, 'Math.ceil');

      const validPattern = /^[\d+\-*/.() ,MathpowsqrtabsroundflooRceil]+$/;
      if (!validPattern.test(safeExpr.replace(/\s/g, ''))) {
        console.error('Invalid characters in expression:', safeExpr);
        return 0;
      }

      const result = new Function(`"use strict"; return (${safeExpr})`)();

      if (typeof result !== 'number' || isNaN(result) || !isFinite(result)) {
        console.error('Invalid calculation result:', result);
        return 0;
      }

      return result;
    } catch (error) {
      console.error('Error evaluating expression:', expr, error);
      return 0;
    }
  }

  private normalizeForControls(raw: any): any {
    if (!raw) return raw;
    const out: any = {};
    const fields = this.getFields();

    for (const f of fields) {
      const val = raw[f.key];
      if (val === undefined) continue;

      switch (f.type) {
        case 'CHECKBOX':
          out[f.key] = val === true || val === 'Y';
          break;

        case 'NUMBER':
          out[f.key] = val === '' || val === null || val === undefined ? null : Number(val);
          break;

        case 'DROPDOWN':
          if ((f as any).multiple) {
            out[f.key] = Array.isArray(val) ? val : val ? [val] : [];
          } else {
            out[f.key] = Array.isArray(val) ? val[0] : (val ?? null);
          }
          break;

        default:
          out[f.key] = val;
      }
    }
    return out;
  }

  private tryPatchFromModel() {
    if (!this.form || !this.modelToPatchToForm) return;
    const normalized = this.normalizeForControls(this.modelToPatchToForm);
    this.form.patchValue(normalized, {emitEvent: false});
  }

  private toValidators(v?: any) {
    if (!v) return [];
    const arr: any = [];
    if (v.required) arr.push(Validators.required);
    if (typeof v.min === 'number') arr.push(Validators.min(v.min));
    if (typeof v.max === 'number') arr.push(Validators.max(v.max));
    if (typeof v.minLength === 'number') arr.push(Validators.minLength(v.minLength));
    if (typeof v.maxLength === 'number') arr.push(Validators.maxLength(v.maxLength));
    if (v.pattern) arr.push(Validators.pattern(v.pattern));

    if (v.custom === 'passwordStrength') arr.push(this.passwordValidator());
    if (v.custom === 'matchPassword') arr.push(this.passwordMatchValidator());

    return arr;
  }

  isInvalid(key: string): boolean {
    if (!this.form) return false;
    const c = this.form.get(key);
    return !!c && c.invalid && (c.touched || c.dirty || this.submittedOnce || this.submitted);
  }

  getValue() {
    this.submittedOnce = true;
    if (!this.form) return;

    this.handleCalculatedFields();

    let payload = this.convertFormValuesFromBoolToChar(this.form?.value);
    payload = this.richEditorService.fixTextEditorInput(payload);

    Object.keys(this.uploadedFiles).forEach((key) => {
      const uploaded = this.uploadedFiles[key];

      payload[key] = {
        fileName: uploaded.file?.name || '',
        filePath: uploaded.newFileName || '',
      };
    });

    if (this.form.valid) {
      this.submitted = true;
      return payload;
    } else {
      this.form.markAllAsTouched();
      return undefined;
    }
  }

  private convertFormValuesFromBoolToChar(formValue: any) {
    const payload: any = {};
    for (const key in formValue) {
      if (typeof formValue[key] === 'boolean') {
        payload[key] = formValue[key] ? 'Y' : 'N';
      } else {
        payload[key] = formValue[key];
      }
    }
    return payload;
  }

  fieldChanged(key: string, value: any) {
    this.onChange.emit({key, value});
  }

  patchValue(data: any) {
    if (!this.form) return;
    const normalized = this.normalizeForControls(data);
    this.form.patchValue(normalized);
    this.cdr.markForCheck();
  }

  getErrorMessage(key: string, customMessages?: any): string {
    if (!this.form) return '';

    const control = this.form.get(key);
    if (!control || !control.errors) return '';

    if (control.errors?.['passwordStrength']) {
      return 'Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character.';
    }

    if (control.errors?.['notMatch']) {
      return 'Passwords do not match.';
    }

    const errorChecks = [
      {type: 'required', message: customMessages?.required || 'This field is required.'},
      {
        type: 'min',
        message: customMessages?.min || `Minimum value is ${control.errors['min']?.min}.`,
      },
      {
        type: 'max',
        message: customMessages?.max || `Maximum value is ${control.errors['max']?.max}.`,
      },
      {
        type: 'minLength',
        message:
          customMessages?.minLength ||
          `Minimum length is ${control.errors['minLength']?.requiredLength} characters.`,
      },
      {
        type: 'maxLength',
        message:
          customMessages?.maxLength ||
          `Maximum length is ${control.errors['maxLength']?.requiredLength} characters.`,
      },
      {type: 'pattern', message: customMessages?.pattern || 'Invalid format.'},
      {
        type: 'email',
        message: customMessages?.email || 'Please enter a valid email address.',
      },
    ];

    for (const check of errorChecks) {
      if (control.errors[check.type]) return check.message;
    }

    return 'Invalid value.';
  }

  sanitize(html: string): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(html);
  }

  togglePassword(key: string) {
    this.showPasswordMap[key] = !this.showPasswordMap[key];
  }

  handleEnter(event: Event) {
    const keyboardEvent = event as KeyboardEvent;
    keyboardEvent.preventDefault();
    this.enterPressed.emit();
  }

  onNumberKeyDown(event: KeyboardEvent, field: any): void {
    const controlKeys = [
      'Backspace',
      'Delete',
      'Tab',
      'Escape',
      'Enter',
      'ArrowLeft',
      'ArrowRight',
      'ArrowUp',
      'ArrowDown',
      'Home',
      'End',
    ];

    const isControlKey = controlKeys.includes(event.key);
    const isCtrlOrCmd = event.ctrlKey || event.metaKey;

    if (isControlKey || isCtrlOrCmd) return;

    if (field.inputRestrict === 'NUMERIC_ONLY') {
      const isDigit = /^[0-9]$/.test(event.key);
      if (!isDigit) {
        event.preventDefault();
      }
    }
  }
}
